// hooks/features/tickets/useProblemCategories.ts
import { useState, useEffect, useCallback } from "react";
import { ProblemCategory, CreateProblemCategoryData, UpdateProblemCategoryData } from "../../../types/features/ProblemCategory";
import { problemCategoryService } from "../../../services/problemCategoryService";
import { PaginatedResponse } from "../../../types/core/PaginatedResponse";

export default function useProblemCategories() {
  const [categories, setCategories] = useState<ProblemCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  const fetchCategories = useCallback(async (page: number = 1, perPage: number = 20, filters?: { search?: string; is_active?: boolean }) => {
    setLoading(true);
    setError(null);
    try {
      const response: PaginatedResponse<ProblemCategory> = await problemCategoryService.getCategories(page, perPage, filters);
      
      setCategories(response.data || []);
      setPagination({
        currentPage: response.current_page || 1,
        lastPage: response.last_page || 1,
        total: response.total || 0,
        perPage: response.per_page || 20,
      });
    } catch (err) {
      setError("Erreur lors du chargement des categories");
      console.error(err);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategoryById = useCallback(async (id: string): Promise<ProblemCategory | null> => {
    setLoading(true);
    setError(null);
    try {
      const category = await problemCategoryService.getCategoryById(id);
      if (!category) setError("Categorie non trouvee");
      return category;
    } catch (err) {
      setError("Erreur lors du chargement de la categorie");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const createCategory = useCallback(
    async (data: CreateProblemCategoryData): Promise<ProblemCategory | null> => {
      setLoading(true);
      setError(null);
      try {
        const newCategory = await problemCategoryService.createCategory(data);
        setCategories((prev) => Array.isArray(prev) ? [newCategory, ...prev] : [newCategory]);
        return newCategory;
      } catch (err) {
        setError("Erreur lors de la creation de la categorie");
        console.error(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const updateCategory = useCallback(
    async (id: string, data: UpdateProblemCategoryData): Promise<ProblemCategory | null> => {
      setLoading(true);
      setError(null);
      try {
        const updatedCategory = await problemCategoryService.updateCategory(id, data);
        setCategories((prev) => Array.isArray(prev) ? prev.map((c) => (c.id === id ? updatedCategory : c)) : []);
        return updatedCategory;
      } catch (err) {
        setError("Erreur lors de la mise a jour de la categorie");
        console.error(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const deleteCategory = useCallback(
    async (id: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        await problemCategoryService.deleteCategory(id);
        setCategories((prev) => Array.isArray(prev) ? prev.filter((c) => c.id !== id) : []);
        return true;
      } catch (err) {
        setError("Erreur lors de la suppression de la categorie");
        console.error(err);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const reorderCategories = useCallback(
    async (categories: string[]): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        await problemCategoryService.reorderCategories(categories);
        return true;
      } catch (err) {
        setError("Erreur lors du reordonnancement des categories");
        console.error(err);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const fetchPublicCategories = useCallback(
    async (entrepriseId: string): Promise<ProblemCategory[]> => {
      setLoading(true);
      setError(null);
      try {
        const categories = await problemCategoryService.getPublicCategories(entrepriseId);
        console.log(categories)
        return categories || [];
      } catch (err) {
        setError("Erreur lors du chargement des categories publiques");
        console.error(err);
        return [];
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return {
    categories: categories || [],
    loading,
    error,
    pagination,
    fetchCategories,
    fetchCategoryById,
    createCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,
    fetchPublicCategories,
  };
}